import { makeAutoObservable, runInAction } from 'mobx';
import clientReviewModel, { ReviewTokenInfo, PublicReview, ProfessionalReviewStats } from '../../model/client/client-review-model';
import { format } from 'date-fns';

/**
 * View-model pour la gestion des avis côté client
 */
class ClientReviewViewModel {
  // États de l'interface utilisateur
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  error: string | null = null;
  success: boolean = false;
  
  // Données du token d'avis
  tokenInfo: ReviewTokenInfo | null = null;
  isTokenValid: boolean = false;
  isTokenExpired: boolean = false;
  isTokenUsed: boolean = false;
  
  // Données du formulaire d'avis
  rating: number = 0;
  comment: string = '';
  
  // Données des avis d'un professionnel
  professionalReviews: PublicReview[] = [];
  professionalStats: ProfessionalReviewStats | null = null;
  reviewSystemEnabled: boolean = true; // Par défaut activé

  constructor() {
    makeAutoObservable(this);
  }
  
  /**
   * Réinitialise les états du formulaire
   */
  resetForm() {
    this.rating = 0;
    this.comment = '';
  }
  
  /**
   * Réinitialise les états d'erreur et de succès
   */
  resetStates() {
    this.error = null;
    this.success = false;
  }
  
  /**
   * Définit l'état de chargement
   */
  setLoading(loading: boolean) {
    this.isLoading = loading;
  }
  
  /**
   * Définit l'état de soumission
   */
  setSubmitting(submitting: boolean) {
    this.isSubmitting = submitting;
  }
  
  /**
   * Définit la note de l'avis
   */
  setRating(rating: number) {
    this.rating = rating;
  }
  
  /**
   * Définit le commentaire de l'avis
   */
  setComment(comment: string) {
    this.comment = comment;
  }
  
  /**
   * Vérifie la validité d'un token d'avis et récupère les informations associées
   */
  async verifyToken(token: string) {
    this.setLoading(true);
    this.resetStates();
    
    try {
      const response = await clientReviewModel.verifyReviewToken(token);
      
      runInAction(() => {
        if (response.success && response.tokenData) {
          // Convertir tokenData en tokenInfo pour la compatibilité
          const tokenData = response.tokenData;

          // Le serveur renvoie companyId/company, mapper vers professionalId/professional
          const companyId = tokenData.companyId;
          const company = tokenData.company;

          // Créer un objet tokenInfo à partir des données du tokenData
          this.tokenInfo = {
            id: tokenData.tokenId,
            token: token,
            bookingId: tokenData.bookingId,
            clientId: tokenData.clientId,
            professionalId: companyId,
            isUsed: false, // Par défaut, puisque le token est valide
            expiresAt: new Date().toISOString(), // Valeur par défaut
            professional: {
              id: companyId,
              firstName: '',
              lastName: '',
              businessName: company?.businessName || '',
              profession: '',
              address: '',
              city: '',
              logo: company?.companyLogo
            },
            booking: {
              id: tokenData.bookingId,
              startTime: tokenData.booking.date || new Date().toISOString(),
              endTime: tokenData.booking.date || new Date().toISOString(),
              serviceId: '',
              service: {
                id: '',
                name: tokenData.booking.service,
                description: '',
                price: 0,
                duration: 0
              }
            },
            client: {
              id: tokenData.clientId,
              firstName: tokenData.client.firstName,
              lastName: tokenData.client.lastName,
              email: ''
            }
          };
          
          this.isTokenValid = true;
          this.isTokenExpired = false;
          this.isTokenUsed = false;
        } else if (response.success && response.tokenInfo) {
          // Cas de compatibilité avec l'ancienne structure
          this.tokenInfo = response.tokenInfo;
          this.isTokenValid = true;
          this.isTokenExpired = false;
          this.isTokenUsed = false;
        } else {
          this.tokenInfo = null;
          this.isTokenValid = false;
          this.isTokenExpired = response.isExpired || false;
          this.isTokenUsed = response.isUsed || false;
          this.error = response.message;
        }
      });
      
      return response.success;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Erreur lors de la vérification du token';
        this.isTokenValid = false;
      });
      return false;
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }
  
  /**
   * Soumet un avis client
   */
  async submitReview() {
    if (!this.tokenInfo) {
      return false;
    }
    
    if (this.rating < 1 || this.rating > 5) {
      return false;
    }
    
    this.setSubmitting(true);
    this.resetStates();
    
    // Inclure les IDs nécessaires extraits du tokenInfo
    const reviewData = {
      token: this.tokenInfo.token,
      clientId: this.tokenInfo.clientId,
      professionalId: this.tokenInfo.professionalId,
      bookingId: this.tokenInfo.bookingId,
      rating: this.rating,
      comment: this.comment.trim()
    };
    
    try {
      const response = await clientReviewModel.submitReview(reviewData);
      
      runInAction(() => {
        if (response.success) {
          this.success = true;
          this.isTokenUsed = true;
        } else {
          this.error = response.message;
        }
      });
      
      return response.success;
    } catch (error: any) {
      runInAction(() => {
        const errorMsg = error.message || 'Erreur lors de la soumission de l\'avis';
        this.error = errorMsg;
      });
      return false;
    } finally {
      runInAction(() => {
        this.setSubmitting(false);
      });
    }
  }
  
  /**
   * Récupère les avis publics d'un professionnel
   */
  async fetchProfessionalReviews(professionalId: string) {
    this.setLoading(true);

    try {
      const response = await clientReviewModel.getProfessionalReviews(professionalId);

      runInAction(() => {
        if (response.success) {
          this.professionalReviews = response.reviews;
          // Mettre à jour le flag reviewSystemEnabled
          this.reviewSystemEnabled = response.reviewSystemEnabled !== false;
        } else {
          const errorMsg = response.message;
          this.error = errorMsg;
        }
      });

      return response.success;
    } catch (error: any) {
      runInAction(() => {
        const errorMsg = error.message || 'Erreur lors de la récupération des avis';
        this.error = errorMsg;
      });
      return false;
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }
  
  /**
   * Récupère les statistiques d'avis d'un professionnel
   */
  async fetchProfessionalStats(professionalId: string) {
    this.setLoading(true);

    try {
      const response = await clientReviewModel.getProfessionalStats(professionalId);

      runInAction(() => {
        if (response.success) {
          this.professionalStats = response.stats;
          this.reviewSystemEnabled = response.reviewSystemEnabled !== false;
        } else {
          const errorMsg = response.message;
          this.error = errorMsg;
        }
      });

      return response.success;
    } catch (error: any) {
      runInAction(() => {
        const errorMsg = error.message || 'Erreur lors de la récupération des statistiques';
        this.error = errorMsg;
      });
      return false;
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }
  
  /**
   * Formate la date d'un avis
   */
  formatReviewDate(dateString: string): string {
    return format(new Date(dateString), 'dd/MM/yyyy');
  }
  
  /**
   * Vérifie si le formulaire d'avis est valide
   */
  get isFormValid(): boolean {
    return this.rating >= 1 && this.rating <= 5;
  }
}

export default new ClientReviewViewModel();
